/**
 * visibleSets — `window.__swimsetDumpVisibleSets()`, the device half of the build 16 parity proof.
 *
 * Returns the sets on screen as JSON, `{screen, swimId, sets: [{name, lengths, meters, stroke}]}`, so
 * the orchestrator can open the owner's Sets tab on one phone and that swim's Club card on a
 * clubmate's phone, run this in each page through Safari Web Inspector, and diff the two answers
 * (docs/build-16.md). Read-only: it reports what the page already drew and sends nothing anywhere.
 * Installed in every build, because the phones being diffed run TestFlight builds.
 *
 * A screen registers a provider while it is showing; the one registered last answers.
 */
import type { SetGroup } from "../setGroups";

export interface VisibleSet {
  name: string;
  lengths: number;
  meters: number;
  /** The one stroke of the set, else "Mixed". */
  stroke: string;
}

export interface VisibleSetsDump {
  screen: string;
  swimId: string | null;
  sets: VisibleSet[];
  /** The Club roster (no card open): every row's swim and sets. */
  rows?: { swimId: string; memberId: string; source: string; sets: VisibleSet[] }[];
}

export function visibleSetsOf(groups: readonly SetGroup[]): VisibleSet[] {
  return groups.map((g) => {
    const strokes = new Set(g.laps.map((l) => l.strokeStyle));
    return {
      name: g.label,
      lengths: g.laps.length,
      meters: Math.round(g.meters),
      stroke: strokes.size === 1 ? g.laps[0].strokeStyle : "Mixed",
    };
  });
}

const providers: (() => VisibleSetsDump)[] = [];

export function registerVisibleSets(provider: () => VisibleSetsDump): () => void {
  providers.push(provider);
  return () => {
    const i = providers.lastIndexOf(provider);
    if (i >= 0) providers.splice(i, 1);
  };
}

export function dumpVisibleSets(): VisibleSetsDump {
  const top = providers[providers.length - 1];
  return top ? top() : { screen: "none", swimId: null, sets: [] };
}

export function installDumpHelper(win: Window): void {
  (win as unknown as { __swimsetDumpVisibleSets?: () => VisibleSetsDump }).__swimsetDumpVisibleSets = dumpVisibleSets;
}
